import { useMemo } from 'react';

import { useComplianceStore } from '../store/complianceStore';
import type { ComplianceStore } from '../store/complianceStore.types';

import type { ComplianceCheckOption, ComplianceChecks } from './useComplianceChecks.types';

/**
 * Convert internal boolean|null policy value to consumer-facing string.
 */
export const getComplianceValue = (value: boolean | null): ComplianceCheckOption => {
  if (value == null) {
    return 'Not Configured';
  }

  return value ? 'Enabled' : 'Disabled';
};

/**
 * Hook to get compliance checks in the OCV-compatible format.
 *
 * @returns ComplianceChecks object with policy states
 */
export const useComplianceChecks = (): ComplianceChecks => {
  const policies = useComplianceStore((state: ComplianceStore) => state.policies);

  return useMemo(() => {
    let policyAllowFeedback: ComplianceCheckOption = 'Not Configured';
    let policyAllowSurvey: ComplianceCheckOption = 'Not Configured';
    let connectedExperiences: 'Enabled' | 'Disabled' | null = null;
    let policyAllowScreenshot: ComplianceCheckOption = 'Not Configured';
    let policyAllowContact: ComplianceCheckOption = 'Not Configured';
    let policyAllowContent: ComplianceCheckOption = 'Not Configured';

    if (policies) {
      policyAllowFeedback = getComplianceValue(policies.UserFeedbackFormEnabled);
      policyAllowSurvey = getComplianceValue(policies.SurveyFeedbackFormEnabled);
      if (policies.ConnectedExperiencesEnabled) {
        connectedExperiences = getComplianceValue(policies.ConnectedExperiencesEnabled) as 'Enabled' | 'Disabled';
      }
      policyAllowScreenshot = getComplianceValue(policies.AllowScreenshots);
      policyAllowContact = getComplianceValue(policies.AllowEmailCollection);
      policyAllowContent = getComplianceValue(policies.AllowLogCollection);
    }

    return {
      authenticationType: 'AAD',
      ageGroup: 'Undefined',
      connectedExperiences,
      policyAllowFeedback,
      policyAllowSurvey,
      policyAllowScreenshot,
      policyAllowContact,
      policyAllowContent,
    };
  }, [policies]);
};
